import { Component } from '@theme/component';

/**
 * Pins the cart page checkout button to the bottom of the viewport on mobile until the
 * user scrolls down to where the button actually sits in the summary.
 *
 * Two details keep the transition seamless:
 *
 * - The button is taken out of flow while pinned, so the host reserves the exact space it
 *   left behind via `--sticky-checkout-reserved`. Keeping the host's box identical in both
 *   states matters: without it the page would shorten on pin, scroll the CTA back into
 *   view, unpin, and oscillate.
 * - Unpinning is keyed on the button's own resting slot reaching the pinned position, so
 *   the button occupies the same pixels either side of the switch and never jumps.
 *
 * @extends {Component<{ checkoutButton: HTMLButtonElement }>}
 */
class StickyCheckout extends Component {
  requiredRefs = ['checkoutButton'];

  /** Matches the mobile breakpoint used by the cart page styles. */
  #mobile = window.matchMedia('(max-width: 749px)');

  #pinned = false;
  #needsMeasure = true;
  #buttonHeight = 0;

  /** Distance the pinned button sits above the viewport bottom; read back from the CSS. */
  #offset = 0;

  /** @type {number | null} */
  #frame = null;

  /**
   * The summary reflows whenever a line item changes or the promo form stacks, so the
   * reserved height has to be re-measured rather than captured once on load.
   */
  #resizeObserver = new ResizeObserver(() => this.#invalidate());

  connectedCallback() {
    super.connectedCallback();

    this.#mobile.addEventListener('change', this.#invalidate);
    window.addEventListener('scroll', this.#schedule, { passive: true });
    window.addEventListener('resize', this.#invalidate, { passive: true });

    this.#resizeObserver.observe(this);
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#mobile.removeEventListener('change', this.#invalidate);
    window.removeEventListener('scroll', this.#schedule);
    window.removeEventListener('resize', this.#invalidate);
    this.#resizeObserver.disconnect();

    if (this.#frame !== null) cancelAnimationFrame(this.#frame);
  }

  #invalidate = () => {
    this.#needsMeasure = true;
    this.#schedule();
  };

  #schedule = () => {
    if (this.#frame !== null) return;

    this.#frame = requestAnimationFrame(() => {
      this.#frame = null;
      this.#update();
    });
  };

  #update() {
    if (!this.#mobile.matches) {
      this.#unpin();
      return;
    }

    // Only measurable while unpinned, when the button is still in flow contributing its row.
    if (this.#needsMeasure && !this.#pinned) this.#measure();
    if (!this.#buttonHeight) return;

    // Valid in both states: the reserved padding keeps the button's resting slot at the
    // top of the host either way.
    const restingBottom = this.getBoundingClientRect().top + this.#buttonHeight;

    if (restingBottom > window.innerHeight - this.#offset) {
      this.#pin();
    } else {
      this.#unpin();
    }
  }

  /**
   * Stores the space the checkout button occupies — its own height plus the grid row gap
   * when other CTAs (accelerated checkout) follow it.
   */
  #measure() {
    const button = this.refs.checkoutButton;
    const height = button?.offsetHeight;
    if (!height) return;

    const gap = this.children.length > 1 ? parseFloat(getComputedStyle(this).rowGap) || 0 : 0;

    this.#buttonHeight = height;
    this.style.setProperty('--sticky-checkout-reserved', `${height + gap}px`);
    this.#needsMeasure = false;
  }

  #pin() {
    if (this.#pinned) return;

    this.#pinned = true;
    this.setAttribute('pinned', '');

    // Resolved from the stylesheet so the unpin threshold can never drift from where the
    // pinned button is actually drawn.
    const offset = parseFloat(getComputedStyle(this.refs.checkoutButton).bottom);
    if (!Number.isNaN(offset)) this.#offset = offset;
  }

  #unpin() {
    if (!this.#pinned) return;

    this.#pinned = false;
    this.removeAttribute('pinned');
    // The button is back in flow; let the next frame confirm the reserved height.
    this.#needsMeasure = true;
  }
}

if (!customElements.get('sticky-checkout')) {
  customElements.define('sticky-checkout', StickyCheckout);
}
